import { el } from "../lib/dom.js";
import { icon } from "../lib/icon.js";
import { AIM, AimGesture, type AimEffect, type AimState, type AimTuning } from "./aim-gesture.js";

/**
 * The assistant's mark in the tab bar (UX spec v3 §C.3; `.aim` in vault.css §8.6).
 *
 * A 64 px hit box with the 56 px drop, its charge ring, its three voice bars and the
 * lift track. Tap → the chat, text first. Hold 400 ms or slide up 96 px → the chat,
 * already listening (it rises 120 ms after the commit, as the motion table says).
 * Pointer capture + `touch-action:none` (CSS) keep the finger on the mark; the
 * gesture starts only inside the box, so the home-indicator swipe always wins.
 * Keyboard and assistive tech: a click with no pointer behind it (`detail === 0`:
 * Enter, Space, a screen reader's activate) is a tap — the single-pointer way in.
 * DCTA-216: the first-run welcome teaches the gesture on this mark (`demo`), never on a drawing of it.
 */
export interface AimHandlers {
  onAssistant(entry: "text" | "voice"): void;
  /** No permission, no engine or offline: nothing charges, a hold says so. */
  voiceOff?(): boolean;
  onVoiceOff?(): void;
}

export interface AimDeps {
  readonly now: () => number;
  readonly frame: (fn: () => void) => void;
  readonly later: (fn: () => void, ms: number) => void;
  readonly vibrate?: (ms: number) => void;
  readonly tuning?: AimTuning;
}

export const browserAimDeps = (): AimDeps => ({
  now: () => performance.now(),
  frame: (fn) => void requestAnimationFrame(fn),
  later: (fn, ms) => void setTimeout(fn, ms),
  vibrate: (ms) => {
    try {
      navigator.vibrate?.(ms);
    } catch {
      // Android only; iOS web has no haptics — the sink and the ring are the feedback.
    }
  },
});

const NS = "http://www.w3.org/2000/svg";
let ids = 0;

/** The charge ring: a track and an arc, r 33 in a 72 box (built node by node). */
function ring(): SVGSVGElement {
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("class", "ring");
  svg.setAttribute("viewBox", "0 0 72 72");
  svg.setAttribute("aria-hidden", "true");
  for (const cls of ["trk", "arc"]) {
    const c = document.createElementNS(NS, "circle");
    c.setAttribute("class", cls);
    c.setAttribute("cx", "36");
    c.setAttribute("cy", "36");
    c.setAttribute("r", "33");
    if (cls === "arc") c.setAttribute("pathLength", "100");
    svg.append(c);
  }
  return svg;
}

/** DCTA-216: one frame of the first-run welcome's lesson, painted on the mark (app/welcome-lesson.ts computes it). */
export interface MarkFrame {
  readonly state: AimState;
  readonly p: number;
  /** How far the drop follows (px): the gesture's own mapping (`dyOf`). */
  readonly dy: number;
  /** The ghost fingertip: px up, opacity, scale. */
  readonly gy: number;
  readonly go: number;
  readonly gs: number;
}

export interface Mark {
  readonly button: HTMLButtonElement;
  /** The hint bubble, the voice-off answer and the description — siblings in `.tabs`. */
  readonly extras: readonly HTMLElement[];
  /**
   * DCTA-216 (10-welcome.md §0.2): paint a lesson frame without touching the gesture machine — `data-state`, `--p`,
   * `--dy` and the ghost (`span.ghost`, aria-hidden, added on the first frame). `null`: the ghost goes and the machine's
   * own view is painted again. A real finger (`pointerdown`, capture phase) ends any demo first, so the machine takes over.
   */
  demo(frame: MarkFrame | null): void;
}

/** The mark behind a tab bar's button (the screens hold only the button). */
const marks = new WeakMap<HTMLButtonElement, Mark>();
export function markOf(button: HTMLButtonElement): Mark | undefined {
  return marks.get(button);
}

export function aimMark(handlers: AimHandlers, deps: AimDeps = browserAimDeps()): Mark {
  const descId = `aim-desc-${String(++ids)}`;
  const button = el("button", { class: "aim", type: "button", data: { state: "idle" }, aria: { label: "Assistant", describedby: descId } }, [
    ring(),
    el("span", { class: "drop", aria: { hidden: "true" } }, [el("i"), el("i"), el("i")]),
    el("span", { class: "lift", aria: { hidden: "true" } }, [
      el("span", { class: "fill" }),
      el("span", { class: "tgt" }, [icon("i-mic")]),
      el("span", { class: "up" }, [icon("i-chev-u")]),
    ]),
  ]);
  const extras = [
    // DCTA-216 W12: one set of verbs everywhere — the welcome's, the tip's and the description's.
    el("span", { class: "aim-hint tip", aria: { hidden: "true" } }, ["Tap to type · ", el("b", {}, ["swipe up to speak"])]),
    // Filled only while shown, so a screen reader hears it when it appears.
    el("span", { class: "aim-hint off", role: "status", aria: { live: "polite" } }),
    el("span", { id: descId, class: "vis-hidden" }, ["Tap to type. Swipe up, or hold, to speak."]),
  ];

  const tuning = deps.tuning ?? AIM;
  const g = new AimGesture(tuning, () => handlers.voiceOff?.() === true);
  let pointer: number | undefined;

  const paint = (): void => {
    const v = g.view;
    button.dataset["state"] = v.state;
    button.style.setProperty("--p", String(v.p));
    button.style.setProperty("--dy", `${String(v.dy)}px`);
  };

  let ghost: HTMLElement | undefined;
  const demo = (frame: MarkFrame | null): void => {
    if (frame === null) {
      if (ghost === undefined) return;
      ghost.remove();
      ghost = undefined;
      for (const k of ["--gy", "--go", "--gs"]) button.style.removeProperty(k);
      paint();
      return;
    }
    if (ghost === undefined) {
      ghost = el("span", { class: "ghost", aria: { hidden: "true" } });
      button.append(ghost);
    }
    button.dataset["state"] = frame.state;
    button.style.setProperty("--p", String(Math.round(frame.p * 1000) / 1000));
    button.style.setProperty("--dy", `${String(frame.dy)}px`);
    button.style.setProperty("--gy", `${frame.gy.toFixed(1).replace(/\.0$/, "")}px`);
    button.style.setProperty("--go", String(Math.round(frame.go * 1000) / 1000));
    button.style.setProperty("--gs", String(Math.round(frame.gs * 1000) / 1000));
  };
  // A real finger ends the lesson before the machine sees the press (capture runs first on the target).
  button.addEventListener("pointerdown", () => demo(null), true);

  const effect = (e: AimEffect | undefined): void => {
    paint();
    if (e === undefined) return;
    if (e.kind === "tap") handlers.onAssistant("text");
    else if (e.kind === "voice-off") handlers.onVoiceOff?.();
    else if (e.kind === "commit") {
      deps.vibrate?.(12);
      deps.later(() => handlers.onAssistant("voice"), 120);
      deps.later(() => {
        g.settle();
        paint();
      }, 600);
    } else {
      deps.later(() => {
        if (g.view.state !== "cancel") return;
        g.settle();
        paint();
      }, 160);
    }
  };

  const loop = (): void => {
    if (!g.active) return;
    effect(g.tick(deps.now()));
    deps.frame(loop);
  };

  button.addEventListener("contextmenu", (e) => e.preventDefault());
  button.addEventListener("pointerdown", (e) => {
    if (e.button > 0 || g.active) return;
    e.preventDefault();
    pointer = e.pointerId;
    try {
      button.setPointerCapture(e.pointerId);
    } catch {
      // A synthetic or already-released pointer: the gesture still runs on this element.
    }
    g.down(e.clientX, e.clientY, deps.now());
    paint();
    deps.frame(loop);
  });
  button.addEventListener("pointermove", (e) => {
    if (e.pointerId !== pointer) return;
    effect(g.move(e.clientX, e.clientY, deps.now()));
  });
  button.addEventListener("pointerup", (e) => {
    if (e.pointerId !== pointer) return;
    pointer = undefined;
    effect(g.up(deps.now()));
  });
  button.addEventListener("pointercancel", (e) => {
    if (e.pointerId !== pointer) return;
    pointer = undefined;
    effect(g.cancel());
  });
  // Enter, Space, a screen reader's activate: a click no pointer made.
  button.addEventListener("click", (e) => {
    if (e.detail === 0) handlers.onAssistant("text");
  });

  const mark: Mark = { button, extras, demo };
  marks.set(button, mark);
  return mark;
}
